import { useState } from "react";
import { Share, Plus, MoreVertical, Download, Apple, Smartphone } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Mobile-only install instructions, three tabs:
 *   - iPhone / iPad  (Safari → Share → Add to Home Screen)
 *   - Android        (Chrome / Samsung Internet → menu → Install app)
 *   - Google Phone   (Pixel → Chrome → menu → Install app, with Pixel-specific copy)
 *
 * Used by both the auto-popup <InstallPrompt /> and the always-on
 * <InstallHelpButton /> at the bottom of the screen. Desktop tab removed —
 * Lauren said she's only worried about mobile users.
 *
 * Props:
 *  - defaultTab: "ios" | "android" | "google" (auto-detected if omitted)
 *  - deferredPrompt: captured beforeinstallprompt event so Android/Pixel
 *    users get a one-tap native install button.
 *  - onInstalled: fired after a successful native install acceptance.
 */
const InstallInstructionsContent = ({ defaultTab, deferredPrompt, onInstalled }) => {
  const inferTab = () => {
    if (defaultTab) return defaultTab;
    if (typeof navigator === "undefined") return "ios";
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) return "ios";
    // Pixel devices report "Pixel" in the UA — default Pixel users to the
    // Google tab so the wording feels addressed to them specifically.
    if (/pixel/.test(ua)) return "google";
    if (/android/.test(ua)) return "android";
    return "ios"; // fall back to iOS for desktop visitors (we hide on desktop anyway)
  };
  const [activeTab, setActiveTab] = useState(inferTab());

  const triggerNativeInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted" && onInstalled) onInstalled();
  };

  const tabs = [
    { id: "ios", label: "iPhone / iPad", short: "iPhone", icon: Apple },
    { id: "android", label: "Android", short: "Android", icon: Smartphone },
    { id: "google", label: "Google Phone", short: "Google", icon: Smartphone },
  ];

  return (
    <div data-testid="install-instructions-content">
      {/* Tab switcher */}
      <div
        className="flex gap-1 p-1 bg-secondary/40 rounded-xl mb-4"
        role="tablist"
        aria-label="Choose your phone"
      >
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(t.id)}
              data-testid={`install-tab-${t.id}`}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                isActive
                  ? "bg-background shadow-sm text-primary"
                  : "text-muted-foreground hover:text-primary"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.short}</span>
            </button>
          );
        })}
      </div>

      {/* iPhone / iPad */}
      {activeTab === "ios" && (
        <div className="space-y-3" data-testid="install-panel-ios">
          <Step n={1} color="blue">
            <p className="text-sm text-primary font-medium">
              Open this site in <strong>Safari</strong>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              "Add to Home Screen" only works in Safari on iOS — not Chrome or Firefox.
            </p>
          </Step>
          <Step n={2} color="blue">
            <p className="text-sm text-primary font-medium">Tap the Share button</p>
            <p className="text-xs text-muted-foreground mt-1">
              The <Share className="h-3 w-3 inline mx-1" /> icon at the bottom (or top on iPad).
            </p>
          </Step>
          <Step n={3} color="blue">
            <p className="text-sm text-primary font-medium">Scroll & tap "Add to Home Screen"</p>
            <p className="text-xs text-muted-foreground mt-1">
              Look for the <Plus className="h-3 w-3 inline mx-1" /> icon in the share sheet.
            </p>
          </Step>
          <Step n={4} color="blue">
            <p className="text-sm text-primary font-medium">Tap "Add" in the top corner</p>
            <p className="text-xs text-muted-foreground mt-1">
              Lala Land's leaf icon will appear on your home screen — tap to launch like any other app.
            </p>
          </Step>
        </div>
      )}

      {/* Android (Samsung, OnePlus, Motorola, etc.) */}
      {activeTab === "android" && (
        <div className="space-y-3" data-testid="install-panel-android">
          {deferredPrompt ? (
            <div className="text-center">
              <Button
                onClick={triggerNativeInstall}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-xl py-6"
                data-testid="install-native-android-btn"
              >
                <Download className="h-5 w-5 mr-2" />
                Install Lala Land
              </Button>
              <p className="text-xs text-muted-foreground mt-3">
                One tap — Chrome will ask to confirm, then Lala Land lives on your home screen.
              </p>
            </div>
          ) : (
            <>
              <Step n={1} color="emerald">
                <p className="text-sm text-primary font-medium">Open this site in <strong>Chrome</strong></p>
                <p className="text-xs text-muted-foreground mt-1">
                  Samsung Internet and Firefox also work, but Chrome is smoothest on Samsung, OnePlus, Motorola, and other Android phones.
                </p>
              </Step>
              <Step n={2} color="emerald">
                <p className="text-sm text-primary font-medium">Tap the menu button</p>
                <p className="text-xs text-muted-foreground mt-1">
                  The <MoreVertical className="h-3 w-3 inline mx-1" /> three-dot button at the top right of Chrome.
                </p>
              </Step>
              <Step n={3} color="emerald">
                <p className="text-sm text-primary font-medium">Tap "Install app" or "Add to Home screen"</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Wording varies by Chrome version — both options install the same way.
                </p>
              </Step>
              <Step n={4} color="emerald">
                <p className="text-sm text-primary font-medium">Tap "Install" to confirm</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Lala Land will appear next to your other apps in your app drawer and home screen.
                </p>
              </Step>
            </>
          )}
        </div>
      )}

      {/* Google Phone (Pixel) */}
      {activeTab === "google" && (
        <div className="space-y-3" data-testid="install-panel-google">
          {deferredPrompt ? (
            <div className="text-center">
              <Button
                onClick={triggerNativeInstall}
                className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-xl py-6"
                data-testid="install-native-google-btn"
              >
                <Download className="h-5 w-5 mr-2" />
                Install Lala Land
              </Button>
              <p className="text-xs text-muted-foreground mt-3">
                One tap — Chrome will ask to confirm, then Lala Land lives on your Pixel home screen.
              </p>
            </div>
          ) : (
            <>
              <Step n={1} color="violet">
                <p className="text-sm text-primary font-medium">
                  Open this site in <strong>Google Chrome</strong>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Chrome is the default browser on Pixel and most Google phones — look for the colorful circle icon.
                </p>
              </Step>
              <Step n={2} color="violet">
                <p className="text-sm text-primary font-medium">Tap the menu (three dots)</p>
                <p className="text-xs text-muted-foreground mt-1">
                  The <MoreVertical className="h-3 w-3 inline mx-1" /> button at the top right of Chrome's address bar.
                </p>
              </Step>
              <Step n={3} color="violet">
                <p className="text-sm text-primary font-medium">Tap "Install app"</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Pixel + Chrome will offer "Install app" with the Lala Land leaf icon. (On older Pixels it may say "Add to Home screen" instead — same result.)
                </p>
              </Step>
              <Step n={4} color="violet">
                <p className="text-sm text-primary font-medium">Tap "Install" to confirm</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Lala Land joins your home screen and Pixel app drawer like any Play Store app — tap once to launch full-screen.
                </p>
              </Step>
            </>
          )}
        </div>
      )}

      {/* Benefits strip */}
      <div className="mt-5 pt-4 border-t border-primary/10">
        <p className="text-xs text-center text-muted-foreground mb-2">Why install?</p>
        <div className="flex justify-center gap-3 sm:gap-4 text-xs text-muted-foreground flex-wrap">
          <span>✨ Full screen</span>
          <span>⚡ Faster access</span>
          <span>📴 Works offline</span>
          <span>🔔 Push reminders</span>
        </div>
      </div>
    </div>
  );
};

// Tiny shared step row to keep the JSX above scannable.
const Step = ({ n, color = "emerald", children }) => {
  const palette = {
    blue: "bg-blue-500",
    emerald: "bg-emerald-500",
    violet: "bg-violet-500",
  }[color] || "bg-emerald-500";
  return (
    <div className="flex items-start gap-3 p-3 bg-secondary/30 rounded-xl">
      <div className={`w-8 h-8 rounded-full ${palette} flex items-center justify-center flex-shrink-0`}>
        <span className="text-white font-bold text-sm">{n}</span>
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
};

export default InstallInstructionsContent;
